import { useCallback, useEffect, useRef } from 'react'
import type { XYPosition } from '@xyflow/react'

/**
 * Merkt sich, wo der Zeiger zuletzt über der Canvas-Fläche stand — der
 * Ankerpunkt, um den die **1:1-Ansicht** (⇧0) heranzoomt.
 *
 * Gibt eine über Renders hinweg stabile Funktion zurück, die den letzten
 * Punkt in Client-Koordinaten liefert oder `null`, wenn der Zeiger den Canvas
 * verlassen hat. Bewusst eine Abfrage-Funktion und kein State: der Wert wird
 * nur im Moment eines Tastendrucks gebraucht, ein Rerender pro Mausbewegung
 * wäre reine Verschwendung.
 *
 * Anders als `useProximity` ohne rAF-Drosselung: hier fällt pro Ereignis nur
 * ein `closest()` an, kein `getBoundingClientRect()` — es gibt also kein
 * Layout-Flush, vor dem zu schützen wäre.
 *
 * @example
 * const getCursorAnchor = useCanvasCursorAnchor(isActiveTab)
 * const anchor = getCursorAnchor() // { x, y } in Client-Koordinaten oder null
 */
export function useCanvasCursorAnchor(enabled: boolean): () => XYPosition | null {
  const anchorRef = useRef<XYPosition | null>(null)

  useEffect(() => {
    if (!enabled) return

    const handleMouseMove = (e: MouseEvent) => {
      // Das Ziel ist über einer Notiz ein Kind der Fläche, nicht sie
      // selbst — `closest` deckt beides ab.
      const overCanvas = (e.target as Element | null)?.closest?.('.react-flow') != null
      anchorRef.current = overCanvas ? { x: e.clientX, y: e.clientY } : null
    }

    window.addEventListener('mousemove', handleMouseMove, { passive: true })
    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      // Sonst überdauerte ein veralteter Punkt den Tabwechsel und die
      // nächste 1:1-Ansicht zoomte auf eine Stelle, an der der Zeiger längst
      // nicht mehr steht.
      anchorRef.current = null
    }
  }, [enabled])

  return useCallback(() => anchorRef.current, [])
}
